/**
 * RecipientItem Constants
 *
 * Centralized constants for the RecipientItem component family.
 */

// =============================================================================
// Animation Timings
// =============================================================================

/** Duration of pop-in animation for new recipients (ms) */
export const POP_ANIMATION_DURATION = 300;

/** Duration of delete slide-out animation (ms) */
export const DELETE_ANIMATION_DURATION = 300;

/** Duration of load-in animation for initial page load (ms) */
export const LOAD_ANIMATION_DURATION = 400;

/** Delay between recipient load-in animations for stagger effect (ms) */
export const LOAD_ANIMATION_STAGGER = 50;

/** Duration of field update success animation (ms) */
export const UPDATE_ANIMATION_DURATION = 600;

/** Consolidated animation durations for hooks */
export const ANIMATION_DURATIONS = {
  /** Duration of pop-in animation for new recipients (ms) */
  POP_IN: POP_ANIMATION_DURATION,
  /** Duration of delete slide-out animation (ms) */
  DELETE: DELETE_ANIMATION_DURATION,
  /** Duration of load-in animation for initial page load (ms) */
  LOAD_IN: LOAD_ANIMATION_DURATION,
  /** Duration of code input exit animation (ms) */
  CODE_INPUT_EXIT: 200,
  /** Duration of just-verified celebration animation (ms) */
  JUST_VERIFIED: 2000,
  /** Duration of field update success animation (ms) */
  UPDATE_SUCCESS: UPDATE_ANIMATION_DURATION,
} as const;

// =============================================================================
// Verification Flow
// =============================================================================

/** Length of verification code */
export const VERIFICATION_CODE_LENGTH = 6;

/** Cooldown period before resend is available (seconds) */
export const RESEND_COOLDOWN_SECONDS = 60;

/** Pattern for valid verification code */
export const VERIFICATION_CODE_PATTERN = /^\d{6}$/;

// =============================================================================
// Avatar
// =============================================================================

/** Default avatar size in pixels */
export const AVATAR_SIZE = 40;

/** Avatar background colors by theme */
export const AVATAR_COLORS = {
  verified: {
    background: 'var(--p-color-bg-fill-success-secondary)',
    text: 'var(--p-color-text-success)',
  },
  unverified: {
    background: 'var(--p-color-bg-fill-warning-secondary)',
    text: 'var(--p-color-text-warning)',
  },
} as const;

// =============================================================================
// Channel Types
// =============================================================================

/** Channel type labels */
export const CHANNEL_LABELS = {
  digest: 'Digest',
  instant: 'Instant',
} as const;

/** Channel type descriptions */
export const CHANNEL_DESCRIPTIONS = {
  digest: 'Daily summary of all alerts',
  instant: 'Immediate notification for each alert',
} as const;

// =============================================================================
// Alert Levels
// =============================================================================

/** Alert level display configuration */
export const ALERT_LEVEL_CONFIG = {
  urgent: {
    label: 'Urgent',
    tone: 'critical' as const,
    description: 'Critical stockouts requiring immediate action',
  },
  warning: {
    label: 'Warning',
    tone: 'warning' as const,
    description: 'Approaching stockout threshold',
  },
  info: {
    label: 'Info',
    tone: 'info' as const,
    description: 'Informational inventory updates',
  },
  healthy: {
    label: 'Restock',
    tone: 'success' as const,
    description: 'Stock levels are healthy',
  },
} as const;

// =============================================================================
// Accessibility
// =============================================================================

/** ARIA labels for interactive elements */
export const ARIA_LABELS = {
  VERIFY_BUTTON: 'Send verification email',
  TEST_BUTTON: 'Send test email',
  EDIT_BUTTON: 'Edit recipient',
  DELETE_BUTTON: 'Delete recipient',
  RESEND_CODE: 'Resend verification code',
  SUBMIT_CODE: 'Submit verification code',
  CODE_INPUT: 'Enter 6-digit verification code',
  ACTIONS_MENU: 'Recipient actions',
  EXPAND_ALERTS: 'Expand alert levels',
  COLLAPSE_ALERTS: 'Collapse alert levels',
  channelToggle: (channel: string, enabled: boolean) =>
    `${enabled ? 'Disable' : 'Enable'} ${channel} notifications`,
  alertLevelToggle: 'Toggle alert level preferences',
} as const;

// =============================================================================
// Test IDs
// =============================================================================

/** Test IDs for component testing */
export const TEST_IDS = {
  recipientItem: 'recipient-item',
  avatar: 'recipient-avatar',
  name: 'recipient-name',
  email: 'recipient-email',
  verifyButton: 'verify-button',
  testButton: 'test-button',
  editButton: 'edit-button',
  deleteButton: 'delete-button',
  codeInput: 'verification-code-input',
  submitButton: 'submit-code-button',
  resendButton: 'resend-code-button',
  deleteModal: 'delete-confirmation-modal',
  channelBadge: (type: string) => `channel-badge-${type}`,
  alertBadge: (level: string) => `alert-badge-${level}`,
} as const;
